// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { useParams } from "react-router-dom";
import { SettingsContext } from "../providers/SettingsContextProvider";
import { GET_VOTER_STATUS } from "../queries/GetVoterStatus";
import { cachePublicationMetadata, loadPublicationList, loadSelectedBallot, PublicationDownloadError, } from "../services/PublishedBallots";
export function useVoterContext(selectedElectionId) {
    var _this = this;
    var _a;
    var _b = useParams(), tenantId = _b.tenantId, eventId = _b.eventId;
    var globalSettings = useContext(SettingsContext).globalSettings;
    var client = useApolloClient();
    // Apollo owns live references/status; the download cache owns immutable JSON.
    // Local state only tracks completion of the selected publication load.
    var result = useQuery(GET_VOTER_STATUS, {
        variables: { electionEventId: eventId || "" },
        skip: globalSettings.DISABLE_AUTH || !tenantId || !eventId,
    });
    var _c = useState(), loaded = _c[0], setLoaded = _c[1];
    var renewed = useRef(false);
    var _d = useState(0), retryCount = _d[0], setRetryCount = _d[1];
    var _e = useState(false), retrying = _e[0], setRetrying = _e[1];
    useEffect(function () {
        renewed.current = false;
    }, [client, tenantId, eventId, selectedElectionId, retryCount]);
    var _f = useState(), downloadError = _f[0], setDownloadError = _f[1];
    var retry = useCallback(function () { return __awaiter(_this, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setRetrying(true);
                    setDownloadError(undefined);
                    setLoaded(undefined);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 4, 5, 6]);
                    if (!(result.error || !result.data)) return [3 /*break*/, 3];
                    return [4 /*yield*/, result.refetch()];
                case 2:
                    _b.sent();
                    _b.label = 3;
                case 3: return [3 /*break*/, 6];
                case 4:
                    _a = _b.sent();
                    return [3 /*break*/, 6];
                case 5:
                    setRetrying(false);
                    // An explicit retry must restart failed downloads even when the
                    // authorized metadata has not changed.
                    setRetryCount(function (count) { return count + 1; });
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); }, [result.refetch, result.error, result.data]);
    useEffect(function () {
        if (!result.data ||
            result.error ||
            retrying ||
            globalSettings.DISABLE_AUTH ||
            !tenantId ||
            !eventId)
            return;
        var active = true;
        setDownloadError(undefined);
        var load = function () { return __awaiter(_this, void 0, void 0, function () {
            var response, attempt, refs, list, styles, _a, error_1;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        response = result.data;
                        attempt = 0;
                        _b.label = 1;
                    case 1:
                        if (!(attempt < 2)) return [3 /*break*/, 12];
                        _b.label = 2;
                    case 2:
                        _b.trys.push([2, 7, , 11]);
                        if (!response)
                            return [2 /*return*/];
                        refs = response.get_ballot_files_urls;
                        if (refs.event_id !== eventId)
                            throw new Error("Published event scope mismatch");
                        return [4 /*yield*/, loadPublicationList(client, refs, selectedElectionId)];
                    case 3:
                        list = _b.sent();
                        if (!selectedElectionId) return [3 /*break*/, 5];
                        return [4 /*yield*/, loadSelectedBallot(client, refs, selectedElectionId)];
                    case 4:
                        _a = [_b.sent()];
                        return [3 /*break*/, 6];
                    case 5:
                        _a = [];
                        _b.label = 6;
                    case 6:
                        styles = _a;
                        if (!active)
                            return [2 /*return*/];
                        cachePublicationMetadata(client, list, tenantId, eventId);
                        setLoaded({
                            source: response,
                            selection: selectedElectionId,
                            data: __assign(__assign({}, list), { sequent_backend_ballot_style: styles, cast_votes: response.cast_votes }),
                        });
                        return [2 /*return*/];
                    case 7:
                        error_1 = _b.sent();
                        if (!active)
                            return [2 /*return*/];
                        if (!(!renewed.current &&
                            attempt === 0 &&
                            error_1 instanceof PublicationDownloadError &&
                            [401, 403].includes(error_1.status))) return [3 /*break*/, 9];
                        renewed.current = true;
                        return [4 /*yield*/, result.refetch()];
                    case 8:
                        response = (_b.sent()).data;
                        return [3 /*break*/, 10];
                    case 9: throw error_1;
                    case 10: return [3 /*break*/, 11];
                    case 11:
                        attempt++;
                        return [3 /*break*/, 1];
                    case 12: return [2 /*return*/];
                }
            });
        }); };
        void load().catch(function () {
            if (active)
                setDownloadError(new Error("Unable to load published ballot data"));
        });
        return function () {
            active = false;
        };
    }, [
        client,
        result.data,
        result.error,
        retryCount,
        retrying,
        selectedElectionId,
        tenantId,
        eventId,
        globalSettings.DISABLE_AUTH,
    ]);
    var data = (loaded === null || loaded === void 0 ? void 0 : loaded.source) === result.data && (loaded === null || loaded === void 0 ? void 0 : loaded.selection) === selectedElectionId
        ? loaded === null || loaded === void 0 ? void 0 : loaded.data
        : undefined;
    return {
        data: data,
        summaries: data === null || data === void 0 ? void 0 : data.summaries,
        error: (_a = result.error) !== null && _a !== void 0 ? _a : downloadError,
        loading: !globalSettings.DISABLE_AUTH &&
            (retrying ||
                (!result.error && !downloadError && (result.loading || (!!result.data && !data)))),
        retry: retry,
        refetch: result.refetch,
    };
}
